import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import CustomMenuList from "../../customComponents/CustomMenuList";
import { useGetClassesQuery } from "../classroom/classApiSlice";
import { useGetTeacherSubjectsQuery } from "../staff/staffApiSlice";
import ScoreSheet from "./ScoreSheet";
import { preloadClassroom } from "../../../app/preloadService";
import { useNetworkStatus } from "../../../hooks/useNetworkStatus";
import db from "../../../app/offlineDb";
import { selectCurrentUser } from "../auth/authSlice";
import { getOfflineContext } from "../../../app/offlineContext";

const QuickEntry = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeSubject, setActiveSubject] = useState(0);
  const [pendingSubject, setPendingSubject] = useState(null);
  const [warnOpen, setWarnOpen] = useState(false);
  const [warnCached, setWarnCached] = useState(false);
  const [openSubjects, setOpenSubjects] = useState(true);
  const { isOnline } = useNetworkStatus();
  const currentUser = useSelector(selectCurrentUser);
  const { data: rawSubjects } = useGetTeacherSubjectsQuery();
  const { data: classrooms } = useGetClassesQuery("all");

  // Deduplicate by (subject id + classroom_id) — guards against duplicate
  // rows in classroom_subject that may exist from historical data.
  const mySubjects = rawSubjects
    ? rawSubjects.filter(
        (subject, index, self) =>
          index ===
          self.findIndex(
            (s) => s.id === subject.id && s.classroom_id === subject.classroom_id
          )
      )
    : undefined;

  // Preload only the actively viewed classroom in the background.
  // This keeps online navigation responsive and still makes refresh/offline
  // usable for the subjects the teacher actually opened.
  useEffect(() => {
    const active = mySubjects?.[activeSubject];
    if (!isOnline || !active?.classroom_id) return;
    const subjectIds = mySubjects
      ?.filter((subject) => subject.classroom_id === active.classroom_id)
      .map((subject) => subject.id) || [];
    preloadClassroom(active.classroom_id, subjectIds, { auth: { user: currentUser } });
  }, [activeSubject, currentUser, isOnline, mySubjects]);

  const handleSubjectChange = async (idx) => {
    if (!isOnline) {
      const targetSubjectId = mySubjects?.[idx]?.id;
      const targetClassroomId = mySubjects?.[idx]?.classroom_id;
      // Check whether this specific subject's records are in Dexie.
      // A row exists (even with data:[]) when the subject was preloaded while online.
      const ctx = getOfflineContext({ auth: { user: currentUser } });
      const cachedRow = targetSubjectId && targetClassroomId && ctx.schoolId && ctx.userId && ctx.termId
        ? await db.records
            .get([ctx.schoolId, ctx.userId, ctx.termId, targetClassroomId, targetSubjectId])
            .catch(() => null)
        : null;
      setPendingSubject(idx);
      setWarnCached(!!cachedRow);
      setWarnOpen(true);
      return;
    }
    setActiveSubject(idx);
    if (isMobile) {
      setOpenSubjects(false);
    }
  };

  const confirmSwitch = () => {
    setActiveSubject(pendingSubject);
    setPendingSubject(null);
    setWarnOpen(false);
    if (isMobile) {
      setOpenSubjects(false);
    }
  };

  const cancelSwitch = () => {
    setPendingSubject(null);
    setWarnOpen(false);
  };

  const subjectsMenu = mySubjects?.map(
    (subject) =>
      `${subject.name}: ${
        classrooms?.find((item) => subject.classroom_id === item.id)?.name
      }`
  );

  return (
    <>
      <Dialog open={warnOpen} onClose={cancelSwitch} maxWidth="xs" fullWidth>
        <DialogTitle>{warnCached ? "You're offline" : "Data not loaded"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {warnCached
              ? "You're offline. This subject's data was loaded before you went offline, so switching is safe — any changes you make will sync automatically when you reconnect."
              : "You're offline and the scores for this subject haven't been loaded onto this device. If you enter scores now, they may overwrite existing scores on the server. Only switch if you're sure you're entering completely fresh data."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {!warnCached && <Button onClick={cancelSwitch} autoFocus>Stay here</Button>}
          <Button onClick={confirmSwitch} color={warnCached ? "primary" : "warning"} variant="contained">
            {warnCached ? "Continue" : "Switch anyway"}
          </Button>
          {warnCached && <Button onClick={cancelSwitch}>Cancel</Button>}
        </DialogActions>
      </Dialog>

      <Grid container columnSpacing={3}>
        <Grid item xs={12} md={4} lg={3}>
          {isMobile ? (
            <Accordion
              expanded={openSubjects}
              onChange={(_, expanded) => setOpenSubjects(expanded)}
              disableGutters
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Subjects</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <CustomMenuList
                  activeTab={activeSubject}
                  setActiveTab={handleSubjectChange}
                  heading="Subjects"
                  list={subjectsMenu}
                  icon={
                    <img
                      src={require(`../../../assets/images/books.png`)}
                      alt="subject"
                      height={25}
                      width={25}
                    />
                  }
                />
              </AccordionDetails>
            </Accordion>
          ) : (
            <CustomMenuList
              activeTab={activeSubject}
              setActiveTab={handleSubjectChange}
              heading="Subjects"
              list={subjectsMenu}
              icon={
                <img
                  src={require(`../../../assets/images/books.png`)}
                  alt="subject"
                  height={25}
                  width={25}
                />
              }
            />
          )}
        </Grid>
        <Grid item xs={12} md={8} lg={9}>
          <ScoreSheet
            subjects={mySubjects || []}
            activeSubject={activeSubject}
            classrooms={classrooms || []}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default QuickEntry;
